// Trigger a browser "save file" for in-memory data. Used by the logbook JSON
// export and by the shared-trip photo downloads; there is no server round-trip
// -- we build a Blob, hand it an object URL, and click a throwaway <a download>
// link.
//
// downloadBlob is the primitive and downloadText is a wrapper on it. The split
// exists because a photo download has its bytes as a Blob already (fetched from
// a presigned S3 URL) and re-serialising it through a string would be both
// lossy and a second copy in memory.

// Download `obj` (any JSON-serializable value) as a pretty-printed .json file.
export function downloadJson(filename, obj) {
  const text = JSON.stringify(obj, null, 2);
  return downloadText(filename, text, 'application/json');
}

// Read a picked File (or Blob) as text. Uses FileReader rather than the newer
// Blob.text() so it works everywhere our tests run (jsdom's File does not
// implement text()).
export function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read file.'));
    reader.readAsText(file);
  });
}

// Download arbitrary text as a file. Best-effort: no-ops (returns false) when
// the DOM/URL APIs aren't available (e.g. SSR), so callers don't have to guard.
export function downloadText(filename, text, mimeType = 'text/plain') {
  if (
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return false;
  }

  return downloadBlob(filename, new Blob([text], { type: mimeType }));
}

// Download a Blob we already hold. Same best-effort contract as downloadText:
// false rather than a throw when the DOM/URL APIs aren't there.
export function downloadBlob(filename, blob) {
  if (
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return false;
  }

  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    // Some browsers only honour a click on a connected node.
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
  return true;
}
